import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, switchMap } from 'rxjs/operators';
import { Subscription, timer } from 'rxjs';

import { BookReviewCreditAcquisitionService } from '../../../shared/services/apis/platform/book-review-credit-acquisition.service';
import { BookCreditAcquisitionRequest } from '../../../shared/services/requests/platform/book-credit-acquisition.request';
import { PaymentMethodsStruct, PaymentMethodInstallmentStruct } from '../../../shared/services/responses/platform/payment-methods.response';

import { PaymentMethodsService } from '../../../shared/services/apis/platform/payment-methods.service';
import { UserCreditCardService } from '../../../shared/services/apis/platform/user-credit-card.service';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { CreditAcquisitionResponse } from '../../../shared/services/responses/platform/credit-acquisition.response';
import { UserCreditCardStruct } from '../../../shared/services/responses/platform/user-credit-card.response';

import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogModule } from '@angular/material/dialog';
import { WaitingPaymentDialogComponent } from '../../book-payment-modal/book-payment-modal.component';

// >>> AJUSTE O PATH CONFORME O SEU PROJETO <<<
// Se o WaitingPaymentDialogComponent está em waiting-payment-dialog, use esta linha:
// Se estiver dentro de book-payment-modal.component, troque para:
// import { WaitingPaymentDialogComponent } from '../../book-payment-modal/book-payment-modal.component';

export interface CreditAquisitionModalData {
  idBookVersion: number;
  idPaymentMethodInstallment?: number; // opcional; pode vir pela lista
  priceLabel?: string;                 // exibição no resumo
  priceValue?: number;                 // base p/ cálculo de parcelas
  onCompleted: (result: { success: boolean; error?: string }) => void;
}

/** Status do backend (C#) */
enum BookReviewCreditPaymentStatus {
  AguardandoPagamento = 1,
  PagamentoEmCurso   = 2,
  Aprovado           = 3,
  Reprovado          = 4,
  Cancelado          = 5
}

const TERMINAL_STATUS = new Set<number>([
  BookReviewCreditPaymentStatus.Aprovado,
  BookReviewCreditPaymentStatus.Reprovado,
  BookReviewCreditPaymentStatus.Cancelado
]);

@Component({
  selector: 'app-book-review-credit-aquisition-modal',
  templateUrl: './book-review-credit-aquisition-modal.component.html',
  styleUrls: ['./book-review-credit-aquisition-modal.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatExpansionModule
  ],
})
export class BookReviewCreditAquisitionModalComponent implements OnInit, OnDestroy {
  isSubmitting = false;
  isLoading = false;

  // controla exibição do conteúdo do form
  showFormContent = true;

  // resumo
  priceLabel?: string;
  priceValue: number = 0;

  // selects
  paymentMethods: PaymentMethodsStruct[] = [];
  paymentMethodInstallments: PaymentMethodInstallmentStruct[] = [];
  selectedPaymentMethod: number | null = null;
  selectedPaymentMethodInstallment: number | null = null;

  // cartões
  userCreditCards: UserCreditCardStruct[] = [];
  selectedCreditCardId: number | null = null;

  // status / PIX
  currentStatusId: number | null = null;
  pixCopyPaste?: string;
  pixQRCode?: string;
  private pollSub?: Subscription;

  // modal de espera
  private waitingRef?: MatDialogRef<WaitingPaymentDialogComponent>;
  private waitingData: {
    title: string;
    message: string;
    pixQRCode?: string | null;
    pixCopyPaste?: string | null;
  } = { title: '', message: '', pixQRCode: null, pixCopyPaste: null };

  // forms
  creditCardForm: FormGroup = this.fb.group({
    holderName: ['', Validators.required],
    number: ['', Validators.required],
    expiryMonth: ['', [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])$/)]],
    expiryYear: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
    ccv: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(4)]],
    email: ['', [Validators.required, Validators.email]],
    cpfCnpj: ['', Validators.required],
    mobilePhone: ['', Validators.required],
    saveCreditCard: [false, Validators.required],
    remoteIp: ['', Validators.required],
    name: ['', Validators.required],
  });

  billingAddressForm: FormGroup = this.fb.group({
    postalCode: ['', Validators.required],
    addressNumber: ['', Validators.required],
    addressComplement: ['']
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CreditAquisitionModalData,
    private ref: MatDialogRef<BookReviewCreditAquisitionModalComponent>,
    private fb: FormBuilder,
    private paymentService: BookReviewCreditAcquisitionService,
    private paymentMethodsService: PaymentMethodsService,
    private userCreditCardService: UserCreditCardService,
    private alertService: AlertService,
    private dialog: MatDialog
  ) {
    // espelha name = holderName
    this.creditCardForm.get('holderName')!.valueChanges.subscribe(v => {
      if (this.creditCardForm.get('name')!.value !== v) {
        this.creditCardForm.get('name')!.setValue(v, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.priceLabel = this.data?.priceLabel;
    this.priceValue = typeof this.data?.priceValue === 'number' ? this.data.priceValue! : 0;

    // checa se já existe pagamento em andamento
    this.checkExistingPaymentOnLoad();
  }

  ngOnDestroy(): void {
    this.stopStatusMonitor();
    this.closeWaitingDialog();
  }

  // --------- Checagem ao carregar ----------

  private checkExistingPaymentOnLoad(): void {
    this.isLoading = true;
    this.paymentService
      .GetStatusCreditAquisition(this.data.idBookVersion, 0)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res: CreditAcquisitionResponse) => {
          if (!res || res.isError) {
            // não há pendência → exibe form e carrega listas
            this.showForm();
            return;
          }

          this.currentStatusId = res.idCreditReviewPaymentStatus ?? null;
          this.pixCopyPaste = res.pixCopyPaste || undefined;
          this.pixQRCode = res.pixQRCode || undefined;

          if (
            this.currentStatusId === BookReviewCreditPaymentStatus.AguardandoPagamento ||
            this.currentStatusId === BookReviewCreditPaymentStatus.PagamentoEmCurso
          ) {
            // há pagamento em andamento → oculta form, abre waiting e inicia polling
            this.showFormContent = false;
            this.openWaitingDialog('Aguardando pagamento', 'Conclua o pagamento para liberarmos seus créditos.');
            this.updateWaitingDialogForStatus();
            this.startStatusMonitor();
          } else {
            // status final ou sem pendência → exibe form
            this.showForm();
          }
        },
        error: () => {
          // erro ao consultar → segue fluxo normal com form
          this.showForm();
        }
      });
  }

  private showForm(): void {
    this.showFormContent = true;
    this.bootstrapLists();
  }

  // --------- Bootstrap (métodos, parcelas, cartões) ----------

  private bootstrapLists(): void {
    this.isLoading = true;

    this.getPaymentMethods(() => {
      this.getUserCreditCards(() => {
        this.isLoading = false;

        // Se veio uma parcela fixa pelo DATA, respeita
        if (this.data?.idPaymentMethodInstallment) {
          this.selectedPaymentMethodInstallment = this.data.idPaymentMethodInstallment;
        }
      });
    });
  }

  getPaymentMethods(done?: () => void): void {
    // CORREÇÃO: volta para o método que existe no service
    this.paymentMethodsService.getCreditsPaymentMethods().subscribe({
      next: (res: any) => {
        if (res.isError) {
          this.alertService.show('Erro ao buscar métodos de pagamento', res.errorDescription, AlertType.error);
          done?.(); return;
        }

        this.paymentMethods = res.listPaymentMethodsStruct || [];

        // Pré-seleciona id=1 (crédito) se existir, senão o primeiro
        const def = this.paymentMethods.find(m => m.idPaymentMethod === 1) || this.paymentMethods[0];
        if (def) {
          this.selectedPaymentMethod = def.idPaymentMethod;
          this.paymentMethodInstallments = def.listPaymentMethodInstallmentStruct || [];
          this.selectedPaymentMethodInstallment =
            this.paymentMethodInstallments.length > 0 ? this.paymentMethodInstallments[0].idPaymentMethodInstallment : null;
        } else {
          this.selectedPaymentMethod = null;
          this.paymentMethodInstallments = [];
          this.selectedPaymentMethodInstallment = null;
        }

        done?.();
      },
      error: (err) => {
        this.alertService.show('Erro inesperado ao buscar métodos de pagamento', err, AlertType.error);
        done?.();
      }
    });
  }

  onPaymentMethodChange(idPaymentMethod: number) {
    this.selectedPaymentMethod = idPaymentMethod;
    const found = this.paymentMethods.find(m => m.idPaymentMethod === idPaymentMethod);
    this.paymentMethodInstallments = found?.listPaymentMethodInstallmentStruct || [];
    this.selectedPaymentMethodInstallment =
      this.paymentMethodInstallments.length > 0 ? this.paymentMethodInstallments[0].idPaymentMethodInstallment : null;
  }

  getUserCreditCards(done?: () => void): void {
    this.userCreditCardService.getUserCreditCards().subscribe({
      next: (res: any) => {
        if (res.isError) {
          this.alertService.show('Erro ao buscar cartões', res.errorDescription, AlertType.error);
          this.userCreditCards = [];
          this.selectedCreditCardId = null;
          done?.(); return;
        }

        this.userCreditCards = res.listUserCreditCardStruct || [];
        if (this.userCreditCards.length > 0) {
          const primary = this.userCreditCards.find(x => x.isPrimaryCard);
          this.selectedCreditCardId = primary
            ? primary.idUserCreditCard
            : this.userCreditCards[0].idUserCreditCard;
        } else {
          this.selectedCreditCardId = null; // força novo cartão
        }

        done?.();
      },
      error: (err) => {
        this.alertService.show('Erro inesperado ao buscar cartões', err, AlertType.error);
        this.userCreditCards = [];
        this.selectedCreditCardId = null;
        done?.();
      }
    });
  }

  // --------- Helpers de UI ----------

  getInstallmentLabel(installment: PaymentMethodInstallmentStruct): string {
    const total = this.priceValue || 0; // total da compra de 1 crédito
    const count = installment.installmentCount || 1;
    const interest = installment.installmentInterest || 0;

    let parcela = 0;
    let totalComJuros = 0;

    if (interest > 0 && count > 1) {
      // Price
      const i = interest / 100;
      const n = count;
      parcela = (total * i) / (1 - Math.pow(1 + i, -n));
      totalComJuros = parcela * n;
    } else {
      parcela = count > 0 ? total / count : total;
      totalComJuros = parcela * count;
    }

    return `${count}x de R$ ${parcela.toFixed(2)} (total R$ ${totalComJuros.toFixed(2)})`;
  }

  get isCreditCardFlow(): boolean {
    // regra atual: cartão de crédito são os métodos id 1 ou 2
    return this.selectedPaymentMethod === 1 || this.selectedPaymentMethod === 2;
  }

  get showNewCreditCardForm(): boolean {
    return this.isCreditCardFlow &&
      (this.selectedCreditCardId === null || this.userCreditCards.length === 0);
  }

  // --------- Submit + WaitingDialog + Monitoramento ----------

  submit(): void {
    if (!this.selectedPaymentMethod || !this.selectedPaymentMethodInstallment) {
      this.alertService.show('Selecione método e parcelamento', '', AlertType.error);
      return;
    }

    if (this.isCreditCardFlow && this.showNewCreditCardForm) {
      if (this.creditCardForm.invalid || this.billingAddressForm.invalid) {
        this.alertService.show('Preencha todos os campos obrigatórios do cartão e endereço.', '', AlertType.error);
        return;
      }
    }

    const req: BookCreditAcquisitionRequest = {
      idBookVersion: this.data.idBookVersion,
      idPaymentMethodInstallment: this.selectedPaymentMethodInstallment,
      idUserCreditCard: this.showNewCreditCardForm ? undefined : this.selectedCreditCardId,
      userCreditCard: this.showNewCreditCardForm ? {
        holderName: this.creditCardForm.value.holderName,
        number: this.creditCardForm.value.number,
        expiryMonth: this.creditCardForm.value.expiryMonth,
        expiryYear: this.creditCardForm.value.expiryYear,
        ccv: this.creditCardForm.value.ccv,
        name: this.creditCardForm.value.holderName,
        email: this.creditCardForm.value.email,
        cpfCnpj: this.creditCardForm.value.cpfCnpj,
        postalCode: this.billingAddressForm.value.postalCode,
        addressNumber: this.billingAddressForm.value.addressNumber,
        addressComplement: this.billingAddressForm.value.addressComplement,
        phone: undefined,
        mobilePhone: this.creditCardForm.value.mobilePhone,
        saveCreditCard: this.creditCardForm.value.saveCreditCard,
        remoteIp: this.creditCardForm.value.remoteIp || '',
      } : undefined
    };

    // Abrir Waiting imediatamente
    this.openWaitingDialog('Processando pagamento', 'Estamos gerando seu pedido. Aguarde...');

    this.isSubmitting = true;
    this.paymentService.createAcquisition(req)
      .pipe(finalize(() => { this.isSubmitting = false; }))
      .subscribe({
        next: (res: CreditAcquisitionResponse) => {
          const ok = !res?.isError;

          // Atualiza status/PIX retornados já na criação
          this.currentStatusId = res?.idCreditReviewPaymentStatus ?? null;
          this.pixCopyPaste = res?.pixCopyPaste || this.pixCopyPaste;
          this.pixQRCode = res?.pixQRCode || this.pixQRCode;
          this.updateWaitingDialogForStatus();

          if (!ok) {
            this.closeWaitingDialog();
            this.finish(false, res?.errorDescription || 'Falha no pagamento');
            return;
          }

          // Decide pelo status atual
          switch (this.currentStatusId) {
            case BookReviewCreditPaymentStatus.Aprovado:
              this.closeWaitingDialog();
              this.finish(true);
              return;
            case BookReviewCreditPaymentStatus.Reprovado:
              this.closeWaitingDialog();
              this.finish(false, res?.errorDescription || 'Pagamento reprovado');
              return;
            case BookReviewCreditPaymentStatus.Cancelado:
              this.closeWaitingDialog();
              this.finish(false, res?.errorDescription || 'Pagamento cancelado');
              return;
            default:
              // AguardandoPagamento / PagamentoEmCurso → mantém waiting aberto e inicia polling
              this.showFormContent = false; // oculta o form enquanto aguarda
              this.startStatusMonitor();
              return;
          }
        },
        error: (err) => {
          this.closeWaitingDialog();
          this.alertService.show('Erro inesperado', err, AlertType.error);
          this.finish(false, err?.error?.errorDescription || 'Falha no pagamento');
        }
      });
  }

  private startStatusMonitor(): void {
    this.stopStatusMonitor();
    // Poll a cada 2s
    this.pollSub = timer(0, 2000).pipe(
      switchMap(() =>
        this.paymentService.GetStatusCreditAquisition(
          this.data.idBookVersion,
          this.currentStatusId ?? 0
        )
      )
    ).subscribe({
      next: (res: CreditAcquisitionResponse) => {
        if (!res) return;

        // Atualiza status/PIX durante o polling e reflete no waiting
        this.currentStatusId = res.idCreditReviewPaymentStatus ?? this.currentStatusId;
        this.pixCopyPaste = res.pixCopyPaste || this.pixCopyPaste;
        this.pixQRCode = res.pixQRCode || this.pixQRCode;
        this.updateWaitingDialogForStatus();

        if (this.currentStatusId != null && TERMINAL_STATUS.has(this.currentStatusId)) {
          const paid = this.currentStatusId === BookReviewCreditPaymentStatus.Aprovado;
          const msg =
            this.currentStatusId === BookReviewCreditPaymentStatus.Reprovado ? (res?.errorDescription || 'Pagamento reprovado') :
            this.currentStatusId === BookReviewCreditPaymentStatus.Cancelado  ? (res?.errorDescription || 'Pagamento cancelado') :
            undefined;

          this.closeWaitingDialog();
          this.finish(paid, paid ? undefined : msg);
        }
      },
      error: () => {
        // Erros intermitentes não encerram o fluxo
      }
    });
  }

  private stopStatusMonitor(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  cancel(): void {
    this.stopStatusMonitor();
    this.closeWaitingDialog();
    this.ref.close();
  }

  private finish(success: boolean, error?: string) {
    this.stopStatusMonitor();
    this.closeWaitingDialog();
    this.data.onCompleted?.({ success, error });
    this.ref.close({ success, error });
  }

  // --------- Waiting dialog helpers ----------

  private openWaitingDialog(title: string, message: string) {
    this.waitingData = { title, message, pixQRCode: null, pixCopyPaste: null };
    this.waitingRef = this.dialog.open(WaitingPaymentDialogComponent, {
      disableClose: true,
      data: this.waitingData
    });
  }

  private updateWaitingDialogForStatus() {
    if (!this.waitingRef) return;

    // Atualiza mensagem conforme status + PIX
    let title = 'Aguardando pagamento';
    let message = 'Conclua o pagamento para liberarmos seus créditos.';

    if (this.currentStatusId === BookReviewCreditPaymentStatus.PagamentoEmCurso) {
      title = 'Pagamento em curso';
      message = 'Estamos processando seu pagamento...';
    }
    if (this.currentStatusId === BookReviewCreditPaymentStatus.Aprovado) {
      title = 'Pagamento aprovado';
      message = 'Finalizando...';
    }
    if (this.currentStatusId === BookReviewCreditPaymentStatus.Reprovado) {
      title = 'Pagamento reprovado';
      message = 'Tente novamente com outro método/cartão.';
    }
    if (this.currentStatusId === BookReviewCreditPaymentStatus.Cancelado) {
      title = 'Pagamento cancelado';
      message = 'A operação foi cancelada.';
    }

    // Mantém a mesma referência de objeto para o dialog
    this.waitingData.title = title;
    this.waitingData.message = message;
    this.waitingData.pixQRCode = this.pixQRCode || null;       // base64 sem prefixo
    this.waitingData.pixCopyPaste = this.pixCopyPaste || null; // copia e cola
  }

  private closeWaitingDialog() {
    try { this.waitingRef?.close(); } catch {}
    this.waitingRef = undefined;
  }
}
